import gql from "graphql-tag";

import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {getAccountSettingsType} from "./__graphql-types__/index.ts";
import type {
    AccountSettingsQueryResult,
    Child,
    PlaybackRate,
    Role,
} from "./types/account-settings-types.ts";

interface QueryResult {
    data?: getAccountSettingsType["response"] | undefined;
    error?: Error | null | undefined;
    loading?: boolean;
}
export const mapDataToResult = ({
    data,
    error,
    loading,
}: QueryResult): AccountSettingsQueryResult => {
    const isError = error != null;

    if (loading || !data || !data.actor || !data.target || !data.sessionHash) {
        return {
            isError,
            isLoading: !!loading,
            actor: null,
            target: null,
            requestInfo: null,
            sessionHash: null,
        };
    }

    let hasDistrictProvidedBirthdate = false;
    let isClasslinkUser = false;

    if (
        data.target.userDistrictInfos &&
        data.target.userDistrictInfos.length > 0
    ) {
        const udi = data.target.userDistrictInfos[0];
        hasDistrictProvidedBirthdate = !!(
            udi?.districtProvidedBirthMonth && udi?.districtProvidedBirthYear
        );

        isClasslinkUser = udi?.rosterSource === "classlink";
    }

    return {
        isError,
        isLoading: !!loading,
        actor: {
            identifier: data.actor.email || data.actor.username,
            isChild: !!data.actor.isChild,
        },
        target: {
            id: data.target.id || "",
            hasSendableEmail: !!data.target.hasSendableEmail,
            isEmailVerified: !!data.target.isEmailVerified,
            isTotpTwoFactorEnabled: !!data.target.isTotpTwoFactorEnabled,
            settings: {
                nickname: data.target.nickname,
                username: data.target.username,
                birthdate: data.target.birthdate,
                userLanguage: data.target.userLanguage,
                region: data.target.region,
                soundOn: !!data.target.soundOn,
                muteVideos: !!data.target.muteVideos,
                showCaptions: !!data.target.showCaptions,
                playbackRate: data.target.playbackRate as PlaybackRate,
                hideVisual: !!data.target.hideVisual,
                prefersReducedMotion: !!data.target.prefersReducedMotion,
                noColorInVideos: !!data.target.noColorInVideos,
            },
            isChild: !!data.target.isChild,
            hasUserOptedOutFromABTesting:
                !!data.target.hasUserOptedOutFromABTesting,
            managingParentEmail: data.target.managingParent
                ? data.target.managingParent.email
                : null,
            underAgeGateParentEmail: data.target.underAgeGate
                ? data.target.underAgeGate.parentEmail
                : null,
            pendingParentEmail: data.target.pendingParentEmail,
            canUpdateParentEmail: data.target.underAgeGate
                ? data.target.underAgeGate.approvalGivenAt === null
                : false,
            daysUntilCutoff:
                data.target.underAgeGate &&
                data.target.underAgeGate.daysUntilCutoff,
            profileRoot: data.target.profileRoot,
            hasPassword: !!data.target.hasPassword,
            canModifyCoaches: !!data.target.canModifyCoaches,
            age: data.target.age,
            authEmails: data.target.authEmails as string[],
            appleId: data.target.appleId,
            applePrimaryEmail: data.target.applePrimaryEmail,
            googleId: data.target.googleId,
            googlePrimaryEmail: data.target.googlePrimaryEmail,
            facebookId: data.target.facebookId,
            facebookPrimaryEmail: data.target.facebookPrimaryEmail,
            microsoftId: data.target.microsoftId,
            microsoftPrimaryEmail: data.target.microsoftPrimaryEmail,
            canAccessDistrictsHomepage: data.target.canAccessDistrictsHomepage,
            userChosenRoles: data.target.userChosenRoles as Role[],
            homepage: data.target.homepage || "",
            isEligibleToBeParent:
                data.target.isParent ||
                !data.target.birthdate ||
                (data.target.age ?? 0) >= 18,
            canSetTeacherAffiliation:
                !data.target.isChild &&
                !data.target.includesCentrallyOwnedDistrictData &&
                (!!data.target.hasClasses || !!data.target.isTeacher),
            maybeManagedChildren: data.target.maybeManagedChildren as Child[],
            unmanagedChildren: data.target.unmanagedChildren as Child[],
            hasChildren: !!data.target.hasChildren,
            includesDistrictOwnedData: data.target.includesDistrictOwnedData,
            includesCentrallyOwnedDistrictData:
                data.target.includesCentrallyOwnedDistrictData,
            isInKhanClassroomDistrict: data.target.isInKhanClassroomDistrict,
            points: data.target.points || 0,
            // badgeCounts is a mapping of badge categories (number) to count as JSON.
            // We sum the values of this mapping to find the total number of badges
            // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'number'.
            badgeCount: Object.values(
                JSON.parse(data.target.badgeCounts || "{}"),
            ).reduce(
                // @ts-expect-error - TS2365 - Operator '+' cannot be applied to types 'boolean' and 'boolean'. | TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'number'. | TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'number'.
                (a, b) => !isNaN(a) + !isNaN(b),
                0,
            ),
            hasDistrictProvidedBirthdate,
            isClasslinkUser,
            affiliationCountryCode: data.target?.affiliationCountryCode,
            schoolAffiliation: data.target?.schoolAffiliation,
            tosForFormalTeacherStatus: data.target?.tosForFormalTeacherStatus,
            isTeacher: data.target?.isTeacher,
        },
        requestInfo: data.target &&
            data.actor && {
                isModifyingChild: data.actor.id !== data.target.id,
                requiresExistingPassword:
                    data.actor.id === data.target.id &&
                    !!data.actor.hasPassword,
            },
        sessionHash: data.sessionHash,
    };
};

export const ACCOUNT_SETTINGS_QUERY: GqlOperation<
    getAccountSettingsType["response"],
    getAccountSettingsType["variables"]
> = gqlOp(gql`
    query getAccountSettings($targetKaid: String) {
        actor: user {
            id
            email
            username
            isChild
            hasPassword
        }
        target: user(kaid: $targetKaid) {
            id
            isChild
            isParent
            isTeacher
            hasUserOptedOutFromABTesting
            tosForFormalTeacherStatus
            affiliationCountryCode
            schoolAffiliation {
                id
                name
                country
                postalCode
                administrativeAreaLevel1
                administrativeAreaLevel2
                locality
            }
            hasSendableEmail
            isEmailVerified
            isTotpTwoFactorEnabled
            nickname
            username
            birthdate
            userLanguage
            region
            soundOn
            muteVideos
            showCaptions
            playbackRate
            hideVisual
            prefersReducedMotion
            noColorInVideos
            pendingParentEmail
            profileRoot
            hasPassword
            canModifyCoaches
            age
            authEmails
            appleId
            applePrimaryEmail
            googleId
            googlePrimaryEmail
            facebookId
            facebookPrimaryEmail
            microsoftId
            microsoftPrimaryEmail
            canAccessDistrictsHomepage
            userChosenRoles
            homepage
            managingParent {
                id
                email
            }
            underAgeGate {
                approvalGivenAt
                parentEmail
                daysUntilCutoff
            }
            maybeManagedChildren {
                id
                nickname
                username
            }
            unmanagedChildren {
                id
                nickname
                username
            }
            hasChildren
            hasClasses
            includesDistrictOwnedData
            includesCentrallyOwnedDistrictData
            isInKhanClassroomDistrict
            badgeCounts
            points
            userDistrictInfos {
                id
                districtProvidedBirthMonth
                districtProvidedBirthYear
                rosterSource
            }
        }
        sessionHash
    }
`);

export default {};
